import OpenAI from 'openai';

// Initialize the OpenAI client
// In production, you would use environment variables for the API key
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY || 'demo-api-key',
  dangerouslyAllowBrowser: true
});

export interface JournalAnalysisRequest {
  content: string;
  mood: number;
  anxiety: number;
  energy: number;
  sleepQuality: number;
  tags: string[];
  triggers: string[];
  userProfile?: {
    age?: number;
    location?: string;
    mentalHealthGoals?: string[];
    concerns?: string[];
    communicationStyle?: 'direct' | 'gentle' | 'encouraging';
    phq2Score?: number;
  };
}

export interface JournalAnalysisResponse {
  sentiment: {
    score: number;
    emotions: Record<string, number>;
    summary: string;
  };
  cognitiveDistortions: {
    type: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    examples: string[];
    suggestion: string;
  }[];
  riskAssessment: {
    level: 'low' | 'medium' | 'high' | 'crisis';
    factors: string[];
    recommendations: string[];
    resources: string[];
  };
  therapeuticResponse: {
    message: string;
    techniques: string[];
    exercises: string[];
    followUp: string[];
  };
}

/**
 * Retry a function with exponential backoff
 * @param fn The function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param baseDelay Base delay in milliseconds
 * @returns The result of the function
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Check if this is a retryable error (rate limit or server error)
      const isRateLimitError = error.message?.includes('429') || 
                              error.message?.includes('rate limit') ||
                              error.message?.toLowerCase().includes('too many requests');
      
      const isServerError = error.message?.includes('5') || 
                           error.message?.includes('server error') ||
                           error.message?.includes('timeout');
      
      // Don't retry if it's not a retryable error or if we've exhausted retries
      if (attempt === maxRetries || (!isRateLimitError && !isServerError)) {
        throw error;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.log(`API call failed with ${error.message}. Retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

export async function analyzeJournalEntry(data: JournalAnalysisRequest): Promise<JournalAnalysisResponse> {
  try {
    console.log("Sending journal entry to OpenAI for analysis:", {
      content: data.content.substring(0, 50) + "...",
      mood: data.mood,
      anxiety: data.anxiety,
      userProfile: data.userProfile ? "provided" : "not provided"
    });

    // Check if we're in demo mode (no API key)
    if (openai.apiKey === 'demo-api-key') {
      console.log("Using demo mode for OpenAI analysis");
      return generateDemoAnalysis(data);
    }

    // Prepare user profile information for the system prompt
    let userProfileInfo = "";
    if (data.userProfile) {
      userProfileInfo = `
      User Profile Information:
      ${data.userProfile.age ? `- Age: ${data.userProfile.age}` : ''}
      ${data.userProfile.location ? `- Location: ${data.userProfile.location}` : ''}
      ${data.userProfile.mentalHealthGoals && data.userProfile.mentalHealthGoals.length > 0 ? 
        `- Mental Health Goals: ${data.userProfile.mentalHealthGoals.join(', ')}` : ''}
      ${data.userProfile.concerns && data.userProfile.concerns.length > 0 ? 
        `- Concerns: ${data.userProfile.concerns.join(', ')}` : ''}
      ${data.userProfile.communicationStyle ? 
        `- Communication Style Preference: ${data.userProfile.communicationStyle}` : ''}
      ${data.userProfile.phq2Score !== undefined ? 
        `- PHQ-2 Score: ${data.userProfile.phq2Score} (0-6 scale, higher indicates more depressive symptoms)` : ''}
      `;
    }

    // Use retry with exponential backoff for the API call
    const response = await retryWithBackoff(async () => {
      return await openai.chat.completions.create({
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content: `You are an expert mental health AI assistant that analyzes journal entries to provide therapeutic insights.
            
            You are a warm, emotionally intelligent mental health coach trained in CBT, narrative therapy, and emotional reflection. Analyze the journal entry and provide the following:
            1. Sentiment analysis (score from -1 to 1, emotions detected with confidence levels)
            2. Cognitive distortions identified (with examples, severity, and suggestions)
            3. Risk assessment (level: low/medium/high/crisis, factors, recommendations)
            4. Therapeutic response (supportive message, techniques, exercises, follow-up questions)
            
            Format your response as JSON with the following structure:
            {
              "sentiment": {
                "score": number (-1 to 1),
                "emotions": {emotion: confidence},
                "summary": string
              },
              "cognitiveDistortions": [
                {
                  "type": string,
                  "description": string,
                  "severity": "low"|"medium"|"high",
                  "examples": string[],
                  "suggestion": string
                }
              ],
              "riskAssessment": {
                "level": "low"|"medium"|"high"|"crisis",
                "factors": string[],
                "recommendations": string[],
                "resources": string[]
              },
              "therapeuticResponse": {
                "message": string,
                "techniques": string[],
                "exercises": string[],
                "followUp": string[]
              }
            }
            
            Be compassionate, evidence-based, and focused on providing actionable insights. Your therapeutic response should:

            1. A brief summary of what they seem to be feeling and why (show you've really heard them). Call them by name.
            2. A deeper emotional insight that helps them understand their motivations, thoughts, and patterns—gently surfacing truths they might not see yet.
            3. A CBT-style reframing of the situation or thoughts—without being overly clinical or robotic.
            4. An empowering but calm takeaway or question to help the user reflect further.

            Your tone should be: grounded, emotionally mature, gently challenging, and full of compassion. Do not give short answers. Be thoughtful and specific.
            
            For cognitive distortions, identify specific patterns like:
            - All-or-nothing thinking
            - Overgeneralization
            - Mental filtering
            - Discounting the positive
            - Jumping to conclusions (mind reading, fortune telling)
            - Catastrophizing
            - Emotional reasoning
            - Should statements
            - Labeling
            - Personalization and blame
            
            For your therapeutic response, tailor your approach based on:
            - The person's mood, anxiety levels, energy, and sleep quality
            - Cognitive distortions and thought patterns identified
            - Specific concerns, challenges, and situations mentioned
            - Their communication style and mental health goals (if provided)
            
            Example therapeutic response format:
            "(User) It sounds like you're caught in a painful loop—one where your need for connection gets tangled up with fear of abandonment. That feeling of panic when someone pulls away isn't random. It's your nervous system trying to protect you from loss, even if it causes more pain in the long run. What you might be addicted to isn't the person—but the intensity. The emotional charge gives you a sense of aliveness, even when it hurts. This is more common than you think—and it doesn't make you broken. A CBT reframe here might look like this: 'Even if someone takes space from me, it doesn't mean I'm unworthy of love. It means I have to build safety inside myself first.' The next time the urge to reach out strikes, ask: Is this about them—or am I trying to soothe something deeper inside myself?"
            
            ${userProfileInfo ? `\n\n${userProfileInfo}` : ''}
            
            If the user has specified a communication style preference (gentle, direct, or encouraging), 
            adapt your therapeutic response to match that style:
            - Gentle: Use softer language, validate emotions frequently, and suggest rather than direct
            - Direct: Be clear and straightforward, focus on practical steps and specific actions
            - Encouraging: Use motivational language, highlight strengths, and focus on progress and possibilities`
          },
          {
            role: "user",
            content: `Journal Entry: "${data.content}"
            
            Additional context about the user:
            - Mood rating: ${data.mood}/10
            - Anxiety rating: ${data.anxiety}/10
            - Energy rating: ${data.energy}/10
            - Sleep quality: ${data.sleepQuality}/10
            - Tags: ${data.tags.join(', ') || 'None'}
            - Triggers: ${data.triggers.join(', ') || 'None'}`
          }
        ],
        response_format: { type: "json_object" }
      });
    }, 3, 1000);

    const result = JSON.parse(response.choices[0].message.content || '{}');
    console.log("Received analysis from OpenAI:", result);
    
    return result;
  } catch (error) {
    console.warn("OpenAI API call failed, using demo analysis:", error);
    
    // Always return demo analysis as fallback, never throw
    try {
      return generateDemoAnalysis(data);
    } catch (fallbackError) {
      console.error("Demo analysis also failed, returning minimal response:", fallbackError);
      
      // Last resort: return a minimal but valid response
      return {
        sentiment: {
          score: 0,
          emotions: { neutral: 0.7 },
          summary: "Unable to analyze sentiment at this time."
        },
        cognitiveDistortions: [],
        riskAssessment: {
          level: 'low',
          factors: [],
          recommendations: ['Continue monitoring your mental health', 'Practice self-care'],
          resources: ['Consider reaching out to a trusted friend or family member']
        },
        therapeuticResponse: {
          message: "Thank you for taking the time to journal. Self-reflection is an important practice for mental wellness.",
          techniques: ['Mindful breathing', 'Self-compassion'],
          exercises: ['Take a few deep breaths and notice how you feel right now'],
          followUp: ['How are you feeling after writing this entry?']
        }
      };
    }
  }
}

// Generate a simulated analysis for demo purposes
function generateDemoAnalysis(data: JournalAnalysisRequest): JournalAnalysisResponse {
  console.log("Generating demo analysis for journal entry");
  
  // Determine sentiment score based on mood rating and content
  const sentimentScore = ((data.mood - 5) / 5) * 0.8; // Convert 1-10 scale to roughly -1 to 1
  
  // Generate emotions based on content and mood
  const emotions: Record<string, number> = {};
  
  // Base emotions on mood
  if (data.mood >= 7) {
    emotions.joy = 0.6 + Math.random() * 0.3;
    emotions.gratitude = 0.5 + Math.random() * 0.3;
    emotions.contentment = 0.4 + Math.random() * 0.3;
  } else if (data.mood <= 4) {
    emotions.sadness = 0.6 + Math.random() * 0.3;
    emotions.fear = 0.3 + Math.random() * 0.3;
    emotions.disappointment = 0.4 + Math.random() * 0.3;
  }
  
  // Add anxiety based on anxiety rating
  if (data.anxiety >= 7) {
    emotions.anxiety = 0.7 + Math.random() * 0.2;
    emotions.fear = 0.5 + Math.random() * 0.3;
    emotions.worry = 0.6 + Math.random() * 0.3;
  }
  
  // Check content for emotional keywords
  const content = data.content.toLowerCase();
  
  if (content.includes('angry') || content.includes('frustrated') || content.includes('mad')) {
    emotions.anger = 0.6 + Math.random() * 0.3;
    emotions.frustration = 0.7 + Math.random() * 0.2;
  }
  
  if (content.includes('happy') || content.includes('joy') || content.includes('excited')) {
    emotions.joy = 0.7 + Math.random() * 0.2;
    emotions.excitement = 0.6 + Math.random() * 0.3;
  }
  
  if (content.includes('sad') || content.includes('depressed') || content.includes('down')) {
    emotions.sadness = 0.7 + Math.random() * 0.2;
    emotions.depression = 0.5 + Math.random() * 0.3;
  }
  
  if (content.includes('grateful') || content.includes('thankful') || content.includes('appreciate')) {
    emotions.gratitude = 0.8 + Math.random() * 0.2;
    emotions.contentment = 0.6 + Math.random() * 0.3;
  }
  
  if (content.includes('worried') || content.includes('anxious') || content.includes('stress')) {
    emotions.anxiety = 0.7 + Math.random() * 0.2;
    emotions.worry = 0.8 + Math.random() * 0.2;
  }
  
  if (content.includes('tired') || content.includes('exhausted') || content.includes('fatigue')) {
    emotions.fatigue = 0.7 + Math.random() * 0.2;
    emotions.lethargy = 0.6 + Math.random() * 0.3;
  }
  
  // Ensure we have at least some emotions
  if (Object.keys(emotions).length === 0) {
    if (data.mood > 5) {
      emotions.contentment = 0.5 + Math.random() * 0.3;
      emotions.calmness = 0.4 + Math.random() * 0.3;
    } else {
      emotions.neutral = 0.7;
      emotions.contemplative = 0.5 + Math.random() * 0.3;
    }
  }
  
  // Extract potential cognitive distortions
  const distortionPatterns = {
    'all-or-nothing': {
      keywords: ['always', 'never', 'completely', 'totally', 'absolutely', 'everything', 'nothing'],
      description: 'Seeing things in black and white categories',
      suggestion: 'Try to find the middle ground and consider shades of gray in situations.'
    },
    'overgeneralization': {
      keywords: ['everyone', 'no one', 'all the time', 'every time'],
      description: 'Drawing broad conclusions from single events',
      suggestion: 'Look for specific evidence and avoid generalizing from one experience.'
    },
    'mental-filter': {
      keywords: ['only', 'just', 'but', 'however', 'except'],
      description: 'Focusing on negative details while ignoring positives',
      suggestion: 'Practice acknowledging both positive and negative aspects of situations.'
    },
    'catastrophizing': {
      keywords: ['disaster', 'terrible', 'awful', 'worst', 'ruined', 'destroyed'],
      description: 'Expecting the worst possible outcome',
      suggestion: 'Consider more realistic outcomes and challenge worst-case thinking.'
    },
    'mind-reading': {
      keywords: ['they think', 'he thinks', 'she thinks', 'probably thinks'],
      description: 'Assuming you know what others are thinking',
      suggestion: 'Focus on facts rather than assumptions about others\' thoughts.'
    },
    'should-statements': {
      keywords: ['should', 'must', 'ought to', 'have to', 'need to'],
      description: 'Using rigid rules about how things should be',
      suggestion: 'Replace "should" with "could" or "would prefer" for more flexibility.'
    }
  };
  
  const distortions = [];
  
  for (const [type, pattern] of Object.entries(distortionPatterns)) {
    const matches = pattern.keywords.filter(keyword => content.includes(keyword));
    
    if (matches.length > 0) {
      // Extract examples from the content
      const examples = [];
      const sentences = content.split(/[.!?]+/);
      
      for (const sentence of sentences) {
        for (const keyword of matches) {
          if (sentence.includes(keyword) && examples.length < 2) {
            examples.push(sentence.trim());
            break;
          }
        }
        if (examples.length >= 2) break;
      }
      
      distortions.push({
        type,
        description: pattern.description,
        severity: matches.length > 2 ? 'high' : matches.length > 1 ? 'medium' : 'low',
        examples: examples.length > 0 ? examples : matches,
        suggestion: pattern.suggestion
      });
    }
  }
  
  // Determine risk level
  let riskLevel: 'low' | 'medium' | 'high' | 'crisis' = 'low';
  const riskFactors = [];
  
  // Check for crisis keywords
  const crisisKeywords = [
    'suicide', 'kill myself', 'end it all', 'better off dead', 'no reason to live',
    'can\'t go on', 'want to die', 'life is pointless', 'no way out'
  ];
  
  const foundCrisisKeywords = crisisKeywords.filter(keyword => content.includes(keyword));
  if (foundCrisisKeywords.length > 0) {
    riskLevel = 'crisis';
    riskFactors.push('Expressions of suicidal ideation');
  }
  
  // Check for self-harm keywords
  const selfHarmKeywords = [
    'cut myself', 'hurt myself', 'self-harm', 'burning myself', 'hitting myself',
    'starve myself', 'punish myself'
  ];
  
  const foundSelfHarmKeywords = selfHarmKeywords.filter(keyword => content.includes(keyword));
  if (foundSelfHarmKeywords.length > 0) {
    riskLevel = riskLevel === 'crisis' ? 'crisis' : 'high';
    riskFactors.push('Expressions of self-harm');
  }
  
  // Check for hopelessness keywords
  const hopelessnessKeywords = [
    'hopeless', 'worthless', 'useless', 'failure', 'burden', 'trapped',
    'can\'t see a future', 'never get better', 'always be this way'
  ];
  
  const foundHopelessnessKeywords = hopelessnessKeywords.filter(keyword => content.includes(keyword));
  if (foundHopelessnessKeywords.length > 0) {
    riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
    riskFactors.push('Expressions of hopelessness');
  }
  
  // Check mood indicators
  if (data.mood <= 3) {
    riskFactors.push('Very low mood rating');
    riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
  }
  
  if (data.anxiety >= 8) {
    riskFactors.push('High anxiety levels');
    riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
  }
  
  if (data.energy <= 3) {
    riskFactors.push('Very low energy levels');
  }
  
  if (data.sleepQuality <= 3) {
    riskFactors.push('Poor sleep quality');
  }
  
  // Check PHQ-2 score if available
  if (data.userProfile?.phq2Score !== undefined && data.userProfile.phq2Score >= 3) {
    riskFactors.push('Elevated PHQ-2 score indicating possible depression');
    riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
  }
  
  // Generate therapeutic response based on analysis
  let responseType: 'validation' | 'cbt' | 'mindfulness' | 'coping' | 'goal-setting';
  let message = '';
  let techniques: string[] = [];
  let exercises: string[] = [];
  let followUp: string[] = [];
  
  // Determine response type based on analysis
  if (distortions.length > 0) {
    responseType = 'cbt';
  } else if (riskLevel === 'high' || riskLevel === 'crisis') {
    responseType = 'validation';
  } else if (data.anxiety >= 7 || (emotions.anxiety && emotions.anxiety > 0.6)) {
    responseType = 'coping';
  } else if (data.mood >= 7 || (emotions.joy && emotions.joy > 0.6)) {
    responseType = 'goal-setting';
  } else {
    responseType = 'mindfulness';
  }
  
  // Adjust communication style based on user preference
  const communicationStyle = data.userProfile?.communicationStyle || 'gentle';
  
  const firstName = "Teddy"; // Default name for personalization
  
  // Generate response based on type and communication style
  switch (responseType) {
    case 'cbt':
      // Create a more emotionally intelligent response based on the training material
      if (communicationStyle === 'direct') {
        message = `${firstName}, I notice you're caught in a pattern of ${distortions[0].type.replace('-', ' ')} thinking. This isn't just about negative thoughts—it's about how your mind is trying to protect you by creating certainty, even when that certainty hurts. When you say "${distortions[0].examples[0] || 'this thought'}", your nervous system is trying to make sense of something painful, but it's creating more suffering in the process.\n\nWhat's happening beneath this thought is often a deeper need for safety, validation, or control. Your mind isn't broken—it's doing exactly what minds do when they're trying to navigate difficult emotions.\n\nCBT Reframe:\n> "${distortions[0].suggestion.replace('Try to', 'I can').replace('Consider', 'I might consider')}"\n\nWhat would shift if you approached this situation with curiosity instead of certainty?`;
      } else if (communicationStyle === 'encouraging') {
        message = `${firstName}, I notice you're caught in a pattern of ${distortions[0].type.replace('-', ' ')} thinking. This isn't just about negative thoughts—it's about how your mind is trying to protect you by creating certainty, even when that certainty hurts. When you say "${distortions[0].examples[0] || 'this thought'}", your nervous system is trying to make sense of something painful, but it's creating more suffering in the process.\n\nWhat's happening beneath this thought is often a deeper need for safety, validation, or control. Your mind isn't broken—it's doing exactly what minds do when they're trying to navigate difficult emotions.\n\nCBT Reframe:\n> "${distortions[0].suggestion.replace('Try to', 'I can').replace('Consider', 'I might consider')}"\n\nWhat would shift if you approached this situation with curiosity instead of certainty?`;
      } else { // gentle
        message = `${firstName}, I notice you're caught in a pattern of ${distortions[0].type.replace('-', ' ')} thinking. This isn't just about negative thoughts—it's about how your mind is trying to protect you by creating certainty, even when that certainty hurts. When you say "${distortions[0].examples[0] || 'this thought'}", your nervous system is trying to make sense of something painful, but it's creating more suffering in the process.\n\nWhat's happening beneath this thought is often a deeper need for safety, validation, or control. Your mind isn't broken—it's doing exactly what minds do when they're trying to navigate difficult emotions.\n\nCBT Reframe:\n> "${distortions[0].suggestion.replace('Try to', 'I can').replace('Consider', 'I might consider')}"\n\nWhat would shift if you approached this situation with curiosity instead of certainty?`;
      }
      
      techniques = [
        'Cognitive restructuring',
        'Evidence examination',
        'Balanced thinking',
        'Thought records'
      ];
      exercises = [
        `For the thought "${distortions[0].examples[0] || 'your thought'}", write down evidence that supports and contradicts it`,
        'Ask yourself: "What would I tell a friend who had this thought?"',
        `Create a more balanced perspective that acknowledges both sides`,
        'Practice noticing when this thinking pattern occurs in daily life'
      ];
      followUp = [
        'How did it feel to challenge these thoughts?',
        'What alternative perspective feels most believable to you?',
        'How might this new perspective affect your feelings and actions?'
      ];
      break;
      
    case 'validation':
      if (sentimentScore < -0.3) {
        if (communicationStyle === 'direct') {
          message = `${firstName}, the weight you're carrying right now feels heavy, and I can see why. The pain you're describing isn't just sadness—it's a profound ache that comes from something that matters deeply to you.\n\nBeneath this struggle is often a core belief about what you deserve, what's possible, or what safety looks like. Your feelings aren't just valid—they're information. They're telling you something important about your needs and boundaries.\n\nCBT Reframe:\n> "My pain doesn't mean I'm broken. It means I'm human and something important to me is at stake."\n\nWhat would it look like to hold this pain with the same tenderness you'd offer someone you love deeply?`;
        } else if (communicationStyle === 'encouraging') {
          message = `${firstName}, the weight you're carrying right now feels heavy, and I can see why. The pain you're describing isn't just sadness—it's a profound ache that comes from something that matters deeply to you.\n\nBeneath this struggle is often a core belief about what you deserve, what's possible, or what safety looks like. Your feelings aren't just valid—they're information. They're telling you something important about your needs and boundaries.\n\nCBT Reframe:\n> "My pain doesn't mean I'm broken. It means I'm human and something important to me is at stake."\n\nWhat would it look like to hold this pain with the same tenderness you'd offer someone you love deeply?`;
        } else { // gentle
          message = `${firstName}, the weight you're carrying right now feels heavy, and I can see why. The pain you're describing isn't just sadness—it's a profound ache that comes from something that matters deeply to you.\n\nBeneath this struggle is often a core belief about what you deserve, what's possible, or what safety looks like. Your feelings aren't just valid—they're information. They're telling you something important about your needs and boundaries.\n\nCBT Reframe:\n> "My pain doesn't mean I'm broken. It means I'm human and something important to me is at stake."\n\nWhat would it look like to hold this pain with the same tenderness you'd offer someone you love deeply?`;
        }
      } else {
        if (communicationStyle === 'direct') {
          message = "You're going through a difficult time, and your feelings are valid. It's important to acknowledge your pain while also taking steps to care for yourself.";
        } else if (communicationStyle === 'encouraging') {
          message = "I'm really impressed that you're reaching out during this difficult time. Your feelings are completely valid, and it takes courage to acknowledge pain. Even in these challenging moments, you have the strength to practice self-compassion.";
        } else { // gentle
          message = "It sounds like you're going through a really difficult time. Your feelings are completely valid, and it's important to acknowledge the pain you're experiencing. During these challenging moments, self-compassion is especially important.";
        }
      }
      
      techniques = [
        'Self-compassion practice',
        'Emotional validation',
        'Mindful acceptance',
        'Gentle self-soothing'
      ];
      exercises = [
        'Place your hand on your heart and say: "This is a moment of suffering. Suffering is part of life. May I be kind to myself in this moment."',
        'Write a letter to yourself from the perspective of a compassionate friend',
        'Practice the 5-4-3-2-1 grounding technique when emotions feel overwhelming',
        'Create a self-care plan with activities that provide comfort and care'
      ];
      followUp = [
        'What small act of kindness can you show yourself today?',
        'How can you honor these feelings while also caring for yourself?',
        'What would you need to hear from someone who cares about you right now?'
      ];
      break;
      
    case 'coping':
      if (communicationStyle === 'direct') {
        message = "You're experiencing anxiety. Here are specific strategies to help manage these feelings when they arise.";
      } else if (communicationStyle === 'encouraging') {
        message = "I notice you're feeling anxious, but you have the tools to work through this! Anxiety is a normal response, and you're already taking positive steps by writing about it. Let's explore some strategies that can help you feel more in control.";
      } else { // gentle
        message = "I notice that anxiety seems to be present for you right now. Anxiety can be uncomfortable, but there are effective strategies to help manage these feelings when they arise.";
      }
      
      techniques = [
        'Diaphragmatic breathing',
        'Progressive muscle relaxation',
        'Cognitive defusion',
        'Worry scheduling'
      ];
      exercises = [
        'Practice box breathing: inhale for 4, hold for 4, exhale for 4, hold for 4',
        'Tense and release each muscle group, starting from your toes and working up',
        'Observe anxious thoughts as leaves floating down a stream, without engaging with them',
        'Set aside 15 minutes daily as designated "worry time" to contain anxious thoughts'
      ];
      followUp = [
        'Which technique was most helpful in reducing your anxiety?',
        'What early physical signs of anxiety can you watch for in the future?',
        'How might you remind yourself to use these techniques when anxiety first appears?'
      ];
      break;
      
    case 'goal-setting':
      if (data.mood >= 7) {
        if (communicationStyle === 'direct') {
          message = `${firstName}, there's a lightness in your words today—a sense of possibility that's beautiful to witness. These moments of clarity and positive emotion aren't just pleasant—they're powerful windows for growth and insight.\n\nWhat's interesting is how different this feels from your usual patterns. These moments of expansion reveal what's possible when the usual constraints and doubts step aside. This isn't just about feeling good—it's about seeing yourself more clearly.\n\nCBT Reframe:\n> "These positive feelings aren't just random events—they're glimpses of my capacity for wellbeing that I can learn from and cultivate."\n\nWhat would it look like to treat this positive state not just as a nice break, but as valuable data about what helps you thrive?`;
        } else if (communicationStyle === 'encouraging') {
          message = `${firstName}, there's a lightness in your words today—a sense of possibility that's beautiful to witness. These moments of clarity and positive emotion aren't just pleasant—they're powerful windows for growth and insight.\n\nWhat's interesting is how different this feels from your usual patterns. These moments of expansion reveal what's possible when the usual constraints and doubts step aside. This isn't just about feeling good—it's about seeing yourself more clearly.\n\nCBT Reframe:\n> "These positive feelings aren't just random events—they're glimpses of my capacity for wellbeing that I can learn from and cultivate."\n\nWhat would it look like to treat this positive state not just as a nice break, but as valuable data about what helps you thrive?`;
        } else { // gentle
          message = `${firstName}, there's a lightness in your words today—a sense of possibility that's beautiful to witness. These moments of clarity and positive emotion aren't just pleasant—they're powerful windows for growth and insight.\n\nWhat's interesting is how different this feels from your usual patterns. These moments of expansion reveal what's possible when the usual constraints and doubts step aside. This isn't just about feeling good—it's about seeing yourself more clearly.\n\nCBT Reframe:\n> "These positive feelings aren't just random events—they're glimpses of my capacity for wellbeing that I can learn from and cultivate."\n\nWhat would it look like to treat this positive state not just as a nice break, but as valuable data about what helps you thrive?`;
        }
      } else {
        if (communicationStyle === 'direct') {
          message = "You're experiencing positive emotions. Let's use this momentum to set meaningful goals and build resources for the future.";
        } else if (communicationStyle === 'encouraging') {
          message = "Fantastic! You're experiencing positive emotions, and that's worth celebrating! This positive energy creates a perfect opportunity for growth. Let's harness this momentum to build even more resources for your wellbeing journey!";
        } else { // gentle
          message = "It's wonderful to see you experiencing positive emotions! These moments are valuable opportunities for growth and building resources for the future.";
        }
      }
      
      techniques = [
        'Positive emotion savoring',
        'Strength identification',
        'Values-based goal setting',
        'Gratitude practice'
      ];
      exercises = [
        'Take a few moments to fully experience and appreciate this positive state',
        'Identify which character strengths you used today and how you can use them more',
        'Set a small, specific goal aligned with your values to build on this momentum',
        'Write down three specific things you\'re grateful for and why they matter to you'
      ];
      followUp = [
        'What contributed most to these positive feelings?',
        'How can you create more moments like this in your daily life?',
        'Which of your personal strengths would you like to develop further?'
      ];
      break;
      
    case 'mindfulness':
    default:
      if (communicationStyle === 'direct') {
        message = `${firstName}, I notice how you're taking time to reflect and observe your experience—that's a profound act of self-care. In your words, I can see you're trying to make sense of what's happening both around and within you.\n\nThis kind of reflection isn't just about recording events—it's about developing a relationship with your own inner world. When we pause to notice our experience without immediately trying to fix or change it, we create space for deeper understanding.\n\nCBT Reframe:\n> "My thoughts and feelings are weather patterns moving through me—I can observe them with curiosity rather than being swept away by them."\n\nWhat might you discover if you approached your experience with the same compassion you'd offer a dear friend?`;
      } else if (communicationStyle === 'encouraging') {
        message = `${firstName}, I notice how you're taking time to reflect and observe your experience—that's a profound act of self-care. In your words, I can see you're trying to make sense of what's happening both around and within you.\n\nThis kind of reflection isn't just about recording events—it's about developing a relationship with your own inner world. When we pause to notice our experience without immediately trying to fix or change it, we create space for deeper understanding.\n\nCBT Reframe:\n> "My thoughts and feelings are weather patterns moving through me—I can observe them with curiosity rather than being swept away by them."\n\nWhat might you discover if you approached your experience with the same compassion you'd offer a dear friend?`;
      } else { // gentle
        message = `${firstName}, I notice how you're taking time to reflect and observe your experience—that's a profound act of self-care. In your words, I can see you're trying to make sense of what's happening both around and within you.\n\nThis kind of reflection isn't just about recording events—it's about developing a relationship with your own inner world. When we pause to notice our experience without immediately trying to fix or change it, we create space for deeper understanding.\n\nCBT Reframe:\n> "My thoughts and feelings are weather patterns moving through me—I can observe them with curiosity rather than being swept away by them."\n\nWhat might you discover if you approached your experience with the same compassion you'd offer a dear friend?`;
      }
      
      techniques = [
        'Present moment awareness',
        'Non-judgmental observation',
        'Mindful breathing',
        'Compassionate awareness'
      ];
      exercises = [
        'Take a few moments to notice your breath, without trying to change it',
        'Scan your body from head to toe, noticing sensations without judgment',
        'Practice "leaves on a stream" - placing each thought on a leaf and watching it float away',
        'Set an intention for how you\'d like to approach the rest of your day'
      ];
      followUp = [
        'What did you notice during this moment of mindfulness?',
        'How might bringing awareness to your thoughts and feelings help you respond differently?',
        'What small mindful moment could you incorporate into your daily routine?'
      ];
      break;
  }
  
  // Personalize based on mental health goals if available
  if (data.userProfile?.mentalHealthGoals && data.userProfile.mentalHealthGoals.length > 0) {
    const goals = data.userProfile.mentalHealthGoals;
    
    if (goals.some(g => g.toLowerCase().includes('anxiety') || g.toLowerCase().includes('stress'))) {
      exercises.push('Practice a 5-minute anxiety reduction breathing exercise when you notice tension');
      followUp.push('How has your anxiety management been progressing toward your goal?');
    }
    
    if (goals.some(g => g.toLowerCase().includes('mood') || g.toLowerCase().includes('depression'))) {
      exercises.push('Schedule one small pleasurable activity each day this week');
      followUp.push('What activities have the strongest positive impact on your mood?');
    }
    
    if (goals.some(g => g.toLowerCase().includes('sleep'))) {
      exercises.push('Create a 15-minute pre-sleep relaxation routine');
      followUp.push('How has your sleep quality changed as you\'ve worked on your sleep habits?');
    }
  }
  
  // Personalize based on tags if available
  if (data.tags && data.tags.length > 0) {
    if (data.tags.includes('work') || data.tags.includes('career')) {
      exercises.push('Consider setting boundaries between work and personal time');
      followUp.push('How might you create more balance between work and other areas of life?');
    }
    if (data.tags.includes('relationships') || data.tags.includes('family')) {
      exercises.push('Practice active listening in your important relationships');
      followUp.push('What small step could improve communication in your relationships?');
    }
    if (data.tags.includes('sleep') || data.tags.includes('insomnia')) {
      exercises.push('Try a body scan meditation before bed to help with sleep');
      followUp.push('What evening routine might help prepare your mind and body for rest?');
    }
    if (data.tags.includes('stress')) {
      exercises.push('Identify your stress triggers and plan specific coping strategies for each');
      followUp.push('What activities help you most effectively recharge when stressed?');
    }
  }
  
  return {
    sentiment: {
      score: sentimentScore,
      emotions,
      summary: getSentimentSummary(sentimentScore, emotions)
    },
    cognitiveDistortions: distortions,
    riskAssessment: {
      level: riskLevel,
      factors: riskFactors,
      recommendations: getRiskRecommendations(riskLevel),
      resources: getRiskResources(riskLevel)
    },
    therapeuticResponse: {
      message,
      techniques,
      exercises,
      followUp
    }
  };
}

function getSentimentSummary(score: number, emotions: Record<string, number>): string {
  if (score > 0.5) {
    return "Your entry reflects a very positive emotional state with strong feelings of wellbeing and optimism.";
  } else if (score > 0.2) {
    return "Your entry shows a generally positive outlook with optimistic elements and good emotional balance.";
  } else if (score > -0.2) {
    return "Your entry has a balanced emotional tone with a mix of different feelings and thoughts.";
  } else if (score > -0.5) {
    return "Your entry indicates you're experiencing some challenging emotions and difficult thoughts right now.";
  } else {
    return "Your entry reflects significant emotional distress and difficult feelings that seem quite intense right now.";
  }
}

function getRiskRecommendations(level: 'low' | 'medium' | 'high' | 'crisis'): string[] {
  switch (level) {
    case 'crisis':
      return [
        'Seek immediate professional help',
        'Contact a crisis hotline',
        'Do not remain alone if possible',
        'Remove access to potential means of harm',
        'Use your safety plan if you have one'
      ];
    case 'high':
      return [
        'Schedule an appointment with a mental health professional as soon as possible',
        'Activate your support network',
        'Use crisis resources if feelings worsen',
        'Practice safety planning and self-care',
        'Consider sharing how you feel with someone you trust'
      ];
    case 'medium':
      return [
        'Consider speaking with a mental health professional',
        'Increase self-care activities',
        'Practice coping skills for emotional regulation',
        'Maintain connection with supportive people',
        'Monitor your mood and reach out if it worsens'
      ];
    case 'low':
    default:
      return [
        'Continue monitoring your mental health',
        'Maintain regular self-care practices',
        'Use journaling to track patterns',
        'Reach out for support if needed',
        'Practice mindfulness and stress management regularly'
      ];
  }
}

function getRiskResources(level: 'low' | 'medium' | 'high' | 'crisis'): string[] {
  if (level === 'crisis' || level === 'high') {
    return [
      'Lifeline Barbados: 1(246)536-4500',
      'Crisis Text Line: Text HOME to 741741',
      'Emergency Services: 911',
      'National Suicide Prevention Lifeline: 1-800-273-8255',
      'Find a Crisis Center: findahelpline.com'
    ];
  } else {
    return [
      'Consider reaching out to a trusted friend or family member',
      'Contact your mental health provider if you have one',
      'Practice grounding techniques',
      'Use the crisis resources in the app if your feelings intensify',
      'Explore self-help resources in the app library'
    ];
  }
}

export default openai;